import { useState } from 'react'
import DeskMobile from '../../surfaces/desk/Mobile'
import DeskDesktop from '../../surfaces/desk/Desktop'
import type { DeskSession } from '../../surfaces/desk/useDeskSession'
import type { TrafficRow } from '../../surfaces/desk/parts/Traffic'

/**
 * The mobile desk, against a mock session — dev only.
 *
 * The live surface needs an authenticated session, which makes the phone
 * arrangement the one thing that cannot be reviewed while building it. This
 * renders the real `Mobile.tsx` over a fabricated `DeskSession` so the layout,
 * the sheets and the composer can be judged at a phone width.
 *
 * Nothing is wired: the acts log rather than call. It answers "does the
 * arrangement read right in a hand", which is the only question it should.
 */

const t = (h: number, m: number) => new Date(Date.UTC(2026, 8, 22, h, m, 0))

const ROWS: TrafficRow[] = [
  {
    id: '0',
    kind: 'rx',
    register: 'Protocol',
    text: 'sked open 10:41 — link established, node N-1',
    at: t(10, 41),
    burstStart: true,
  },
  {
    id: '1',
    kind: 'rx',
    register: 'Voice',
    text: "bridge is up. panel in the sun, batteries green",
    at: t(10, 42),
  },
  {
    id: '2',
    kind: 'tx',
    register: 'Operator',
    text: 'copy. what does the portal look like',
    at: t(10, 43),
  },
  {
    /*
     * A TIME DIVIDER. Since the per-line stamp rail went (2026-08-17), seams are
     * the only place time is stated in the feed — so the fixture has to carry one
     * or the harness cannot show what the transcript now looks like.
     */
    id: 's1',
    kind: 'seam',
    text: '40 minutes pass at the desk',
  },
  {
    id: '3',
    kind: 'rx',
    register: 'Voice',
    text: "concrete, poured rough, and a steel door someone has already been at. hinges are new-ish. that is not in the papers",
    at: t(10, 45),
    burstStart: true,
  },
  {
    /*
     * A LOG passage as the live surface actually delivers it: the engine's "//"
     * marker is consumed into a seam by the session hook, and what reaches a row
     * is the prose without it. The fixture used to carry the raw marker, which
     * made the harness lie about what the feed contains.
     */
    id: '4',
    kind: 'rx',
    register: 'Log',
    text: 'portal: concrete plug 1949, steel door retrofitted, hinges recent. no record of the retrofit in the seal invoice.',
    at: t(10, 47),
  },
]

export default function MobileHarness() {
  const [nextCall, setNextCall] = useState<Date | null>(t(20, 0))
  const [arrangement, setArrangement] = useState<string | null>(
    'walk the first drift, mark the legs',
  )

  const session: DeskSession = {
    packId: 'silfurberg',
    scenario: undefined,
    station: 'SILFURBERG',
    role: 'the desk',

    sessionId: 'mock',
    starting: false,
    error: null,
    begin: () => {},
    autoBegin: false,

    opening: {
      action: 'OPEN THE LINK',
      cards: [
        { id: 'title', kind: 'title', title: 'SILFURBERG', body: '*the Glerdalur Descent*' },
        {
          id: 'note',
          kind: 'paper',
          title: 'A note, sent ahead',
          body: 'i need a desk.\n\nten days under. you hold the surface.',
        },
      ],
    },
    openingRunning: false,
    openingSettled: true,
    finishOpening: () => {},

    rows: ROWS,
    working: false,
    terminated: false,
    terminationReason: null,

    now: t(11, 24),
    arrangement,
    nextCall,
    setNextCall,
    plan: null,
    setPlan: () => {},
    skedLabel: '— sign off until the morning sked —',

    send: () => {},
    arrange: setArrangement,
    jump: () => {},
    signOff: () => {},
    jumpBusy: false,
    skedBusy: false,
  }

  /*
   * `?surface=desktop` renders the OTHER sibling over the same mock. It is here
   * because the operator's book was extracted out of `Instruments` so both
   * surfaces could share it, and a refactor of a shipping component deserves to
   * be looked at rather than trusted to the typechecker.
   */
  if (new URLSearchParams(window.location.search).get('surface') === 'desktop') {
    return <DeskDesktop session={session} />
  }

  return <DeskMobile session={session} />
}
